/* =============================================================
   Hope Journey — custom experience (clean rewrite)
   - NO Lenis (use native browser scroll for smoothness)
   - NO ThreeJS (lighter on the device)
   - Features: Your Picks (swipe deck + wishlist) on every page,
     hover buttons on cards, hero slider, horizontal sliders, light GSAP reveals.
   ============================================================= */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const WISHLIST_KEY = 'hopeJourneyWishlist';

  const ready = (cb) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb, { once: true });
    else cb();
  };

  ready(() => {
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    mountProductDeck();
    mountCardActions();
    mountSideDrawer();
    lockMenuDrawerScroll();
    if (!reduceMotion && !designMode) mountReveals();
  });

  /* ---------------------------------------------------------------
     Icons
  --------------------------------------------------------------- */
  function heartIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.4-7.5-10.2A4.3 4.3 0 0 1 12 7.4a4.3 4.3 0 0 1 7.5 2.9c0 5.8-7.5 10.2-7.5 10.2Z" fill="currentColor"/></svg>';
  }
  function nopeIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  }
  function cartIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 8h13l-1.4 8.2a2 2 0 0 1-2 1.7H9.4a2 2 0 0 1-2-1.6L5.8 5H3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 21h.01M17 21h.01" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>';
  }
  function closeIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------------------------------------------------------------
     Read products from cards on the page
  --------------------------------------------------------------- */
  function getProductFromCard(card) {
    const link = card.querySelector('.card__heading a, a.full-unstyled-link');
    const image = card.querySelector('.card__media img, img');
    const price = card.querySelector('.price');
    const variantInput = card.querySelector('input.product-variant-id[name="id"], input[name="id"]');
    const quickAdd = card.querySelector('.quick-add__submit');
    const href = link && link.href;
    if (!href || !image) return null;
    return {
      href,
      title: link.textContent.trim().replace(/\s+/g, ' '),
      image: image.currentSrc || image.src,
      price: price ? price.innerText.trim().replace(/Regular price|Sale price|From/g, '').trim() : '',
      variantId: variantInput ? variantInput.value : '',
      quickAddId: quickAdd ? quickAdd.id : '',
    };
  }

  function getDeckProducts() {
    const seen = new Set();
    return [...document.querySelectorAll('.product-card-wrapper')]
      .map(getProductFromCard)
      .filter((p) => {
        if (!p || seen.has(p.href)) return false;
        seen.add(p.href);
        return true;
      })
      .slice(0, 24);
  }

  // Fallback: load products when the current page has no cards (so Your Picks works on every page).
  async function fetchFallbackProducts() {
    try {
      const res = await fetch('/products.json?limit=24', { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.products || [])
        .map((p) => {
          const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
          const image = (p.images || [])[0];
          if (!image) return null;
          return {
            href: `/products/${p.handle}`,
            title: p.title,
            image: typeof image === 'string' ? image : image.src,
            price: variant ? formatMoney(variant.price) : '',
            variantId: variant ? String(variant.id) : '',
            quickAddId: '',
          };
        })
        .filter(Boolean);
    } catch (_e) {
      return [];
    }
  }

  function formatMoney(value) {
    const num = Number(value);
    if (Number.isNaN(num)) return String(value || '');
    return '$' + num.toFixed(2);
  }

  // Cart page? (hide Top Picks on /cart)
  function isCartPage() {
    return Boolean(document.querySelector('cart-items')) || /\/cart\/?$/.test(window.location.pathname);
  }

  /* ---------------------------------------------------------------
     Wishlist (localStorage)
  --------------------------------------------------------------- */
  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (_e) {
      return [];
    }
  }
  function writeWishlist(list) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  }

  /* ---------------------------------------------------------------
     YOUR PICKS — deck swipe + wishlist
  --------------------------------------------------------------- */
  function mountProductDeck() {
    if (document.querySelector('.hj-deck')) return;
    if (isCartPage()) return; // hide Top Picks on the cart page

    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'hj-deck-launcher';
    launcher.setAttribute('aria-label', 'Open Top Picks');
    launcher.innerHTML = `${heartIcon()}<span>Your Picks</span>`;

    const overlay = document.createElement('div');
    overlay.className = 'hj-deck-overlay';

    const deck = document.createElement('aside');
    deck.className = 'hj-deck';
    deck.setAttribute('role', 'dialog');
    deck.setAttribute('aria-modal', 'true');
    deck.setAttribute('aria-label', 'Top Picks For You');
    deck.setAttribute('aria-hidden', 'true');
    deck.innerHTML = `
      <div class="hj-deck__head">
        <div class="hj-deck__title-wrap">
          <p class="hj-deck__eyebrow">Swipe to shop</p>
          <h2 class="hj-deck__title">Top Picks For You</h2>
        </div>
        <button type="button" class="hj-deck__tab" data-deck-tab="wish" aria-label="View wishlist">
          ${heartIcon()}<span>Wishlist</span><small class="hj-deck__count">0</small>
        </button>
        <button type="button" class="hj-deck__close" aria-label="Close">${closeIcon()}</button>
      </div>
      <div class="hj-deck__body">
        <div class="hj-deck__play">
          <div class="hj-deck__stage" aria-live="polite"></div>
          <div class="hj-deck__actions">
            <button type="button" class="hj-deck__action hj-deck__action--skip" data-deck-action="skip" aria-label="Skip">
              ${nopeIcon()}<span>Skip</span>
            </button>
            <button type="button" class="hj-deck__action hj-deck__action--cart" data-deck-action="cart" aria-label="Add to cart">
              ${cartIcon()}<span>Add</span>
            </button>
            <button type="button" class="hj-deck__action hj-deck__action--wish" data-deck-action="wish" aria-label="Save to wishlist">
              ${heartIcon()}<span>Save</span>
            </button>
          </div>
          <p class="hj-deck__hint">Swipe right to save · swipe up to add to cart · swipe left to skip</p>
        </div>
        <div class="hj-deck__saved">
          <div class="hj-deck__saved-head">
            <button type="button" class="hj-deck__saved-back" data-deck-tab="play" aria-label="Back to swiping">‹ Back</button>
            <p class="hj-deck__saved-title">Wishlist <span class="hj-deck__count">0</span></p>
          </div>
          <div class="hj-deck__saved-list"></div>
        </div>
      </div>
    `;

    document.body.append(launcher, overlay, deck);

    const state = {
      index: 0,
      products: getDeckProducts(),
      saved: readWishlist(),
      stage: deck.querySelector('.hj-deck__stage'),
      fallbackTried: false,
    };

    const lockScroll = (lock) => {
      document.documentElement.style.overflow = lock ? 'hidden' : '';
      document.body.style.overflow = lock ? 'hidden' : '';
    };

    const open = async () => {
      deck.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.classList.add('hj-deck-open');
      deck.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      deck.querySelector('.hj-deck__close').focus();

      if (state.products.length < 3 && !state.fallbackTried) {
        state.fallbackTried = true;
        state.stage.innerHTML = '<div class="hj-deck__empty"><p>Loading products…</p></div>';
        const extra = await fetchFallbackProducts();
        const seen = new Set(state.products.map((p) => p.href));
        extra.forEach((p) => {
          if (!seen.has(p.href)) {
            seen.add(p.href);
            state.products.push(p);
          }
        });
      }
      // Preload ảnh để lật card không bị nháy trắng (nhất là mobile/mạng chậm).
      state.products.forEach((p) => {
        const im = new Image();
        im.src = p.image;
      });
      renderDeck(state);
      renderSaved(state);
    };

    const close = () => {
      deck.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.classList.remove('hj-deck-open');
      deck.setAttribute('aria-hidden', 'true');
      deck.classList.remove('is-wishlist-view');
      lockScroll(false);
      launcher.focus();
    };

    launcher.addEventListener('click', open);
    overlay.addEventListener('click', close);
    deck.querySelector('.hj-deck__close').addEventListener('click', close);

    deck.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-deck-tab]');
      if (tab) {
        deck.classList.toggle('is-wishlist-view', tab.dataset.deckTab === 'wish');
        return;
      }
      const remove = event.target.closest('[data-wish-remove]');
      if (remove) {
        event.preventDefault();
        event.stopPropagation();
        state.saved = state.saved.filter((p) => p.href !== remove.dataset.wishRemove);
        writeWishlist(state.saved);
        renderSaved(state);
        return;
      }
      const action = event.target.closest('[data-deck-action]');
      if (action) swipeProduct(state, action.dataset.deckAction);
    });

    document.addEventListener('keydown', (event) => {
      if (!deck.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') swipeProduct(state, 'wish');
      if (event.key === 'ArrowLeft') swipeProduct(state, 'skip');
      if (event.key === 'ArrowUp') swipeProduct(state, 'cart');
    });
  }

  function renderDeck(state) {
    state.stage.innerHTML = '';
    const visible = state.products.slice(state.index, state.index + 3).reverse();
    if (!visible.length) {
      state.stage.innerHTML = `
        <div class="hj-deck__empty">
          <h3>All done 🎉</h3>
          <p>Your wishlist is saved. Reload the page to keep swiping.</p>
        </div>`;
      return;
    }
    visible.forEach((product, layer) => {
      const depth = visible.length - layer - 1;
      const card = document.createElement('article');
      card.className = 'hj-card';
      card.style.setProperty('--depth', depth);
      card.innerHTML = `
        <div class="hj-card__media">
          <img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy" draggable="false">
          <span class="hj-card__stamp hj-card__stamp--wish">${heartIcon()}</span>
          <span class="hj-card__stamp hj-card__stamp--skip">${nopeIcon()}</span>
          <span class="hj-card__stamp hj-card__stamp--cart">${cartIcon()}</span>
        </div>
        <div class="hj-card__info">
          <h3>${escapeHtml(product.title)}</h3>
          <p>${escapeHtml(product.price)}</p>
        </div>`;
      if (depth === 0) bindSwipe(card, state, product);
      state.stage.append(card);
    });
  }

  function bindSwipe(card, state, product) {
    // GSAP "sở hữu" transform suốt cả quá trình kéo -> bay ra, nên không bị
    // giật/nháy (flash) khi bàn giao animation — quan trọng nhất trên mobile (touch).
    const useGsap = Boolean(window.gsap) && !reduceMotion;
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let dy = 0;
    let dragging = false;
    let moved = false;

    const setPos = (x, y, rot) => {
      if (useGsap) window.gsap.set(card, { x, y, rotation: rot });
      else card.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;
    };

    const onMove = (event) => {
      if (!dragging) return;
      dx = event.clientX - startX;
      dy = event.clientY - startY;
      moved = moved || Math.abs(dx) > 6 || Math.abs(dy) > 6;
      setPos(dx, dy, dx / 22);
      card.classList.toggle('is-wishing', dx > 50);
      card.classList.toggle('is-skipping', dx < -50);
      card.classList.toggle('is-carting', dy < -50);
    };

    const detach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      detach();
      // Decide by DOMINANT AXIS: a horizontal swipe (even if angled up) -> wish/skip;
      // only a clear upward push (vertical axis dominant) counts as cart.
      const horizontal = Math.abs(dx) >= Math.abs(dy);
      if (horizontal && dx > 110) swipeProduct(state, 'wish');
      else if (horizontal && dx < -110) swipeProduct(state, 'skip');
      else if (!horizontal && dy < -110) swipeProduct(state, 'cart');
      else {
        // Trả card về chỗ cũ một cách mượt (không snap cứng).
        card.classList.remove('is-wishing', 'is-skipping', 'is-carting');
        if (useGsap) window.gsap.to(card, { x: 0, y: 0, rotation: 0, duration: 0.32, ease: 'power3.out' });
        else card.style.transform = '';
      }
    };

    card.addEventListener('pointerdown', (event) => {
      if (event.target.closest('a, button')) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startY = event.clientY;
      dx = 0;
      dy = 0;
      // Giữ pointer trên card để touch không bị mất event khi ngón tay đi ra ngoài.
      if (event.pointerId != null && card.setPointerCapture) {
        try {
          card.setPointerCapture(event.pointerId);
        } catch (_e) {
          /* no-op */
        }
      }
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });

    // Click (no drag) on the image/card = open the product page.
    card.addEventListener('click', (event) => {
      if (moved || event.target.closest('a, button')) return;
      if (product.href) window.location.href = product.href;
    });
  }

  async function swipeProduct(state, action) {
    if (state.busy) return; // prevent overlapping swipes while a card is flying out
    const product = state.products[state.index];
    const active = state.stage.querySelector('.hj-card[style*="--depth: 0"]') || state.stage.lastElementChild;
    if (!product || !active) return;
    state.busy = true;

    active.classList.add(action === 'wish' ? 'is-wishing' : action === 'cart' ? 'is-carting' : 'is-skipping');

    // Mobile (màn nhỏ): bay VỪA ĐỦ ra khỏi mép + chậm hơn chút + ease giảm tốc
    // -> như cú "ném" mượt, không vụt nhanh giống flash. Desktop giữ nguyên cảm giác cũ.
    const small = window.matchMedia('(max-width: 749px)').matches;
    const offX = small ? (window.innerWidth || 380) * 1.05 : 560;
    const offY = small ? (window.innerHeight || 720) * 0.85 : 560;
    const flyDur = small ? 0.5 : 0.36;
    const flyEase = small ? 'power2.out' : 'power2.in';
    const target = action === 'cart' ? { x: 0, y: -offY, r: 0 } : action === 'wish' ? { x: offX, y: -40, r: 16 } : { x: -offX, y: 40, r: -16 };

    // Side-effects run IMMEDIATELY & IN BACKGROUND (don't block the swipe flow):
    // - wishlist: write to localStorage instantly (synchronous)
    // - add to cart: fire in the background, NOT awaited
    if (action === 'wish') {
      const saved = readWishlist();
      if (!saved.some((p) => p.href === product.href)) {
        saved.unshift({ href: product.href, title: product.title, image: product.image, price: product.price });
        writeWishlist(saved);
        state.saved = saved;
        renderSaved(state);
        pulseLauncher();
      }
    } else if (action === 'cart') {
      addToCart(product).catch(() => {}); // background; errors don't affect swiping
    }

    // Only AWAIT the fly-out animation (fast), then unlock for the next swipe.
    try {
      if (window.gsap && !reduceMotion) {
        await new Promise((resolve) => {
          window.gsap.to(active, { x: target.x, y: target.y, rotation: target.r, autoAlpha: 0, duration: flyDur, ease: flyEase, onComplete: resolve });
        });
      } else {
        active.style.transform = `translate(${target.x}px, ${target.y}px) rotate(${target.r}deg)`;
        active.style.opacity = '0';
        await new Promise((r) => setTimeout(r, reduceMotion ? 0 : 240));
      }
    } finally {
      state.index += 1;
      renderDeck(state);
      state.busy = false;
    }
  }

  function renderSaved(state) {
    const list = document.querySelector('.hj-deck__saved-list');
    const counts = document.querySelectorAll('.hj-deck__count');
    if (!list) return;
    counts.forEach((c) => (c.textContent = String(state.saved.length)));
    if (!state.saved.length) {
      list.innerHTML = '<div class="hj-deck__saved-empty">Swipe right to save the pieces you love here.</div>';
      return;
    }
    list.innerHTML = state.saved
      .map(
        (p) => `
        <div class="hj-deck__saved-item">
          <a class="hj-deck__saved-link" href="${p.href}">
            <img src="${p.image}" alt="" draggable="false">
            <span><strong>${escapeHtml(p.title)}</strong><small>${escapeHtml(p.price || '')}</small></span>
          </a>
          <button type="button" class="hj-deck__saved-remove" data-wish-remove="${p.href}" aria-label="Remove from wishlist">${closeIcon()}</button>
        </div>`
      )
      .join('');
  }

  function pulseLauncher() {
    const launcher = document.querySelector('.hj-deck-launcher');
    if (!launcher) return;
    launcher.classList.remove('is-pulse');
    requestAnimationFrame(() => launcher.classList.add('is-pulse'));
    setTimeout(() => launcher.classList.remove('is-pulse'), 700);
  }

  async function addToCart(product) {
    const variantId = product.variantId || (await fetchVariantId(product.href));
    if (variantId && window.routes && window.routes.cart_add_url) {
      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (res.ok) {
          await refreshCartBubble();
          return true;
        }
      } catch (_e) {
        /* fallback below */
      }
    }
    if (product.quickAddId) {
      const btn = document.getElementById(product.quickAddId);
      if (btn) btn.click();
    }
    return false;
  }

  async function fetchVariantId(href) {
    try {
      const url = new URL(href, window.location.origin);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return '';
      const product = await res.json();
      const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
      return variant ? String(variant.id) : '';
    } catch (_e) {
      return '';
    }
  }

  async function refreshCartBubble() {
    // Re-render the cart-icon-bubble section so the badge updates INSTANTLY (no reload).
    try {
      const res = await fetch('/?section_id=cart-icon-bubble');
      if (res.ok) {
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('#cart-icon-bubble');
        const current = document.querySelector('#cart-icon-bubble');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          return;
        }
      }
    } catch (_e) {
      /* try the fallback below */
    }
    // Fallback: update the count directly from /cart.js
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((b) => (b.textContent = cart.item_count));
    } catch (_e2) {
      /* best-effort */
    }
  }

  /* ---------------------------------------------------------------
     Hover buttons on the product card (skip / cart / wish)
  --------------------------------------------------------------- */
  function mountCardActions() {
    document.querySelectorAll('.product-card-wrapper').forEach((card) => {
      const host = card.querySelector('.card__inner') || card.querySelector('.card');
      if (!host || host.querySelector('.hj-quick')) return;
      const product = getProductFromCard(card);
      if (!product) return;

      const actions = document.createElement('div');
      actions.className = 'hj-quick';
      actions.innerHTML = `
        <button type="button" class="hj-quick__btn hj-quick__btn--cart" data-quick="cart" aria-label="Add to cart">${cartIcon()}</button>
        <button type="button" class="hj-quick__btn hj-quick__btn--wish" data-quick="wish" aria-label="Save to wishlist">${heartIcon()}</button>`;
      host.append(actions);

      actions.addEventListener('click', async (event) => {
        const btn = event.target.closest('[data-quick]');
        if (!btn) return;
        event.preventDefault();
        event.stopPropagation();
        const kind = btn.dataset.quick;
        btn.classList.add('is-pop');
        setTimeout(() => btn.classList.remove('is-pop'), 600);
        if (kind === 'wish') {
          const saved = readWishlist();
          if (!saved.some((p) => p.href === product.href)) {
            saved.unshift({ href: product.href, title: product.title, image: product.image, price: product.price });
            writeWishlist(saved);
            pulseLauncher();
          }
        } else if (kind === 'cart') {
          await addToCart(product);
        }
      });
    });
  }

  /* ---------------------------------------------------------------
     Left drawer menu (left hamburger button, slides in from the left,
     holds the full menu — cloned from the header's inline menu).
  --------------------------------------------------------------- */
  function mountSideDrawer() {
    if (document.querySelector('.hj-drawer')) return;
    const nav = document.querySelector('.header__inline-menu .list-menu--inline');
    const header = document.querySelector('.header');
    if (!nav || !header) return;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'hj-drawer-toggle';
    toggle.setAttribute('aria-label', 'Open menu');
    toggle.innerHTML = '<span></span><span></span><span></span>';

    const overlay = document.createElement('div');
    overlay.className = 'hj-drawer-overlay';

    const drawer = document.createElement('aside');
    drawer.className = 'hj-drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML = `<button type="button" class="hj-drawer__close" aria-label="Close">${closeIcon()}</button><nav class="hj-drawer__menu"></nav>`;
    drawer.querySelector('.hj-drawer__menu').append(nav.cloneNode(true));

    document.body.append(toggle, overlay, drawer);

    // Khoá cuộn trang khi drawer mở (iOS-proof): chặn touchmove NGOÀI drawer,
    // vẫn cho cuộn bên trong drawer nếu menu dài. Drawer là position:fixed nên
    // không bao giờ trôi theo scroll dù mở ở bất kỳ vị trí cuộn nào.
    const onDrawerTouchMove = (e) => {
      if (e.target.closest('.hj-drawer')) return;
      e.preventDefault();
    };
    const open = () => {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('hj-menu-locked');
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', onDrawerTouchMove, { passive: false });
    };
    const close = () => {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('hj-menu-locked');
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', onDrawerTouchMove, { passive: false });
    };
    toggle.addEventListener('click', open);
    overlay.addEventListener('click', close);
    drawer.querySelector('.hj-drawer__close').addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Mobile/tablet (<990px): Dawn ẩn menu ngang & hiện nút ☰. Dùng CHÍNH nút ☰ đó
    // để mở drawer cố định này — thay cho menu gốc Dawn (position:absolute, neo dưới
    // header nên bị lệch/trôi sau khi cuộn trang). Desktop (>=990) vẫn dùng nút tròn.
    const dawnHamburger = document.querySelector('summary.header__icon--menu');
    if (dawnHamburger) {
      dawnHamburger.addEventListener(
        'click',
        (e) => {
          if (!window.matchMedia('(max-width: 989px)').matches) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          if (drawer.classList.contains('is-open')) close();
          else open();
        },
        true
      );
    }
  }

  /* ---------------------------------------------------------------
     Khoá scroll trang khi MENU GỐC của Dawn (mobile/tablet) đang mở.
     Dawn dựng menu bằng position:absolute neo dưới header -> khi cuộn trang,
     menu trôi theo. Ở đây chặn cuộn trang (iOS-proof) trong lúc menu mở,
     nhưng VẪN cho cuộn nội bộ bên trong .menu-drawer.
  --------------------------------------------------------------- */
  function lockMenuDrawerScroll() {
    const details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;
    const onTouchMove = (event) => {
      if (event.target.closest('.menu-drawer')) return; // cho phép cuộn TRONG menu
      event.preventDefault(); // chặn cuộn trang phía sau
    };
    details.addEventListener('toggle', () => {
      if (details.open) {
        document.documentElement.classList.add('hj-menu-locked');
        document.addEventListener('touchmove', onTouchMove, { passive: false });
      } else {
        document.documentElement.classList.remove('hj-menu-locked');
        document.removeEventListener('touchmove', onTouchMove, { passive: false });
      }
    });
  }

  /* ---------------------------------------------------------------
     Light reveals with GSAP (native scroll, no Lenis) — translate Y only,
     NO opacity so content is never hidden/stuck.
  --------------------------------------------------------------- */
  function mountReveals() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    gsap.utils.toArray('.banner__heading, .rich-text__heading, .collection-hero__title, .main-page-title, .title-wrapper-with-link h2').forEach((el) => {
      gsap.fromTo(el, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 92%', once: true } });
    });

    ScrollTrigger.batch('.product-card-wrapper, .multicolumn-card, .collage__item', {
      start: 'top 94%',
      once: true,
      onEnter: (batch) => gsap.fromTo(batch, { y: 16 }, { y: 0, duration: 0.5, stagger: 0.04, ease: 'power2.out' }),
    });
  }
})();